<template>
	<div class="tcontainer">
		<Toast />
		<div class="clearfix ttitle">
			<router-link to="/center/xray-report" class="pull-right btn btn-secondary">
				<i class="fa fa-arrow-left"></i>
			</router-link>
			<div class="title pull-left">Merged X-Ray Reports</div>
		</div>

		<form class="merge-form" @submit.prevent="submit">
			<div class="row">
				<div class="col-md-3">
					<label>From Date/Time</label>
					<Calendar v-model="form.from_date" :maxDate="nowDate" dateFormat="dd/mm/yy" hourFormat="12"
						:showTime="true" :showIcon="true" :manualInput="false" class="w-100" @date-select="onFromDateChange" />
				</div>
				<div class="col-md-3">
					<label>To Date/Time</label>
					<Calendar v-model="form.to_date" :minDate="form.from_date" :maxDate="toMaxDate" dateFormat="dd/mm/yy"
						hourFormat="12" :showTime="true" :showIcon="true" :manualInput="false" class="w-100" @date-select="onToDateChange" />
				</div>
				<div class="col-md-3 include-box">
					<label>
						<input type="checkbox" v-model="form.include_images">
						Include Images
					</label>
				</div>
				<div class="col-md-3 submit-box">
					<button class="btn btn-primary" type="button" :disabled="!!saving" @click="submit('pdf')">
						<span v-if="saving == 'pdf'"><i class="fa fa-spinner fa-spin"></i> Generating...</span>
						<span v-else><i class="fa fa-file-pdf"></i> Generate PDF</span>
					</button>
					<button class="btn btn-success zip-button" type="button" :disabled="!!saving" @click="submit('zip')">
						<span v-if="saving == 'zip'"><i class="fa fa-spinner fa-spin"></i> Generating...</span>
						<span v-else><i class="fa fa-file-archive-o"></i> Generate ZIP</span>
					</button>
				</div>
			</div>
			<div v-if="message" class="alert" :class="messageType" style="margin-top: 12px;">
				{{ message }}
			</div>
		</form>

		<div class="clearfix" style="margin: 18px 0 8px;">
			<button class="btn btn-info btn-sm pull-right" @click="loadJobs" :disabled="loading">
				<i class="fa fa-refresh"></i>
			</button>
			<h5>Generated Files</h5>
		</div>

		<table class="table table-bordered table-striped">
			<thead>
				<tr>
					<th>ID</th>
					<th>Date Range</th>
					<th>Images</th>
					<th>Type</th>
					<th>Reports</th>
					<th>Status</th>
					<th>Created At</th>
					<th>File</th>
				</tr>
			</thead>
			<tbody>
				<tr v-if="loading">
					<td colspan="8" class="text-center">Loading...</td>
				</tr>
				<tr v-else-if="!jobs.length">
					<td colspan="8" class="text-center">No merged reports generated yet.</td>
				</tr>
				<tr v-for="job in jobs" :key="job.id">
					<td>{{ job.id }}</td>
					<td>{{ format(job.from_date, "DD/MM/YY h:mmA") }} - {{ format(job.to_date, "DD/MM/YY h:mmA") }}</td>
					<td>{{ job.include_images == 'y' ? 'Yes' : 'No' }}</td>
					<td>{{ fileTypeLabel(job.file_type) }}</td>
					<td>{{ job.report_count }}</td>
					<td>
						<span class="badge" :class="statusClass(job.status)">{{ job.status }}</span>
						<div v-if="job.error_message" class="text-danger small">{{ job.error_message }}</div>
					</td>
					<td>{{ format(job.created_at, "DD/MM/YY h:mmA") }}</td>
					<td>
						<a v-if="job.status == 'completed' && job.filename" class="btn btn-sm btn-success"
							:href="wapiBaseUrl + '/xray_reports/' + job.filename" target="_blank">
							<i class="fa fa-download"></i> Download {{ fileTypeLabel(job.file_type) }}
						</a>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
import Calendar from 'primevue/calendar';

export default {
	components: {
		Calendar
	},
	data() {
		let now = new Date();
		let todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
		return {
			form: {
				from_date: todayStart,
				to_date: now,
				include_images: false,
			},
			nowDate: now,
			jobs: [],
			wapiBaseUrl: '',
			saving: false,
			loading: false,
			message: '',
			messageType: 'alert-info',
			refreshInterval: null,
		}
	},
	mounted() {
		this.loadJobs();
	},
	unmounted() {
		this.stopRefresh();
	},
	computed: {
		toMaxDate() {
			let now = this.nowDate || new Date();
			if (!this.form.from_date) return now;
			let max = new Date(this.form.from_date.getTime() + 48 * 60 * 60 * 1000);
			return max.getTime() > now.getTime() ? now : max;
		}
	},
	watch: {
		'form.from_date'() {
			this.onFromDateChange();
		},
		'form.to_date'() {
			this.onToDateChange();
		}
	},
	methods: {
		padDatePart(value) {
			return String(value).padStart(2, '0');
		},
		formatDateForSubmit(value) {
			if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
			return value.getFullYear() + '-' +
				this.padDatePart(value.getMonth() + 1) + '-' +
				this.padDatePart(value.getDate()) + 'T' +
				this.padDatePart(value.getHours()) + ':' +
				this.padDatePart(value.getMinutes());
		},
		refreshNowDate() {
			this.nowDate = new Date();
		},
		limitToDate() {
			if (!this.form.from_date) return;
			let max = this.toMaxDate;
			if (!this.form.to_date || this.form.to_date.getTime() < this.form.from_date.getTime()) {
				this.form.to_date = new Date(this.form.from_date);
			}
			if (this.form.to_date.getTime() > max.getTime()) {
				this.form.to_date = new Date(max);
			}
		},
		onFromDateChange() {
			this.refreshNowDate();
			if (this.form.from_date && this.form.from_date.getTime() > this.nowDate.getTime()) {
				this.form.from_date = new Date(this.nowDate);
			}
			this.limitToDate();
		},
		onToDateChange() {
			this.refreshNowDate();
			this.limitToDate();
		},
		dateRangeHours() {
			if (!this.form.from_date || !this.form.to_date) return 0;
			let from = this.form.from_date;
			let to = this.form.to_date;
			return (to.getTime() - from.getTime()) / (60 * 60 * 1000);
		},
		hasActiveJob() {
			return this.jobs.some(job => job.status == 'pending' || job.status == 'processing');
		},
		startRefresh() {
			if (this.refreshInterval) return;
			this.refreshInterval = setInterval(this.loadJobs, 10000);
		},
		stopRefresh() {
			if (!this.refreshInterval) return;
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
		},
		updateRefresh() {
			if (this.hasActiveJob()) {
				this.startRefresh();
			} else {
				this.stopRefresh();
			}
		},
		async loadJobs() {
			this.loading = true;
			try {
				let res = await this.post("center/load-merged-xray-reports", {});
				if (res.success) {
					this.jobs = res.rows || [];
					this.wapiBaseUrl = res.wapi_base_url || '';
					this.updateRefresh();
				}
			} finally {
				this.loading = false;
			}
		},
		async submit(fileType) {
			this.message = '';
			this.refreshNowDate();
			this.limitToDate();
			fileType = fileType == 'zip' ? 'zip' : 'pdf';
			let hours = this.dateRangeHours();
			if (Number.isNaN(hours) || hours < 0) {
				this.messageType = 'alert-danger';
				this.message = 'Please select a valid date range.';
				return;
			}
			if (hours > 48) {
				this.messageType = 'alert-danger';
				this.message = 'Date range can be maximum two days.';
				return;
			}

			this.saving = fileType;
			try {
				let payload = Object.assign({}, this.form, {
					from_date: this.formatDateForSubmit(this.form.from_date),
					to_date: this.formatDateForSubmit(this.form.to_date),
					file_type: fileType
				});
				let res = await this.post("center/generate-merged-xray-report", payload);
				if (res.success) {
					this.messageType = 'alert-success';
					this.message = (fileType == 'zip' ? 'ZIP' : 'PDF') + ' job created for ' + res.report_count + ' reports.';
					await this.loadJobs();
				} else {
					this.messageType = 'alert-danger';
					this.message = res.message || 'Failed to create merge job.';
				}
			} catch (err) {
				this.messageType = 'alert-danger';
				this.message = 'Failed to create merge job.';
			} finally {
				this.saving = false;
			}
		},
		fileTypeLabel(fileType) {
			return fileType == 'zip' ? 'ZIP' : 'PDF';
		},
		statusClass(status) {
			if (status == 'completed') return 'bg-success';
			if (status == 'failed') return 'bg-danger';
			if (status == 'processing') return 'bg-info';
			return 'bg-warning';
		}
	}
}
</script>

<style scoped>
.merge-form {
	background: #fff;
	border: 1px solid #ddd;
	padding: 15px;
	margin-top: 12px;
}
.include-box,
.submit-box {
	padding-top: 26px;
}
.submit-box .btn {
	margin-right: 6px;
	margin-bottom: 6px;
}
.zip-button {
	background-color: #28a745;
	border-color: #28a745;
}
.badge {
	text-transform: capitalize;
}
</style>
